// Transparent overlay header — the fade backdrop shows through behind it.
// Carries the agent name, connection state, and the menu / settings buttons.

import { useChatStore } from '../../store/chatStore';
import { useSettingsStore } from '../../store/settingsStore';
import { MenuIcon, SettingsIcon, WindowIcon } from '../ui/Icons';

const CONN_LABEL: Record<string, string> = {
  connecting: 'Connecting…',
  disconnected: 'Offline — reconnect',
  error: 'Offline — reconnect',
};

interface Props {
  onOpenSidebar: () => void;
  onOpenSettings: () => void;
  // When a snippet is open, mobile shows a pill that flips back to its
  // full-viewport view. `null` (desktop split, or no snippet) hides the pill.
  snippetTitle?: string | null;
  onShowSnippet?: () => void;
}

export function Header({ onOpenSidebar, onOpenSettings, snippetTitle, onShowSnippet }: Props) {
  const connection = useChatStore((s) => s.connection);
  const connect = useChatStore((s) => s.connect);
  const agents = useChatStore((s) => s.agents);
  const activeId = useChatStore((s) => s.activeAgentId);
  const sidebarPinned = useSettingsStore((s) => s.sidebarPinned);

  const active = agents.find((a) => a.id === activeId);
  const title = active?.name ?? (agents.length === 0 ? 'Niu' : 'Messages');

  const connected = connection.kind === 'connected';
  const dot =
    connection.kind === 'connected'
      ? 'bg-success-text'
      : connection.kind === 'connecting'
        ? 'bg-accent animate-status-dot'
        : 'bg-error-text';

  return (
    <header className="absolute inset-x-0 top-0 z-20 flex h-12 items-center gap-2 px-3">
      <button
        type="button"
        onClick={onOpenSidebar}
        aria-label="Open menu"
        className={`-ml-1 h-8 w-8 items-center justify-center rounded-lg text-text-secondary hover:bg-divider/40 hover:text-text-primary ${
          sidebarPinned ? 'flex md:hidden' : 'flex'
        }`}
      >
        <MenuIcon />
      </button>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <h1 className="truncate text-[15px] font-semibold text-text-primary">{title}</h1>
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
        {!connected ? (
          <button
            type="button"
            onClick={() => connect()}
            className="shrink-0 text-[11px] text-text-secondary transition-colors hover:text-text-primary"
          >
            {CONN_LABEL[connection.kind] ?? 'Offline'}
          </button>
        ) : null}
      </div>
      {snippetTitle && onShowSnippet ? (
        <button
          type="button"
          onClick={onShowSnippet}
          aria-label="Show live view"
          className="flex min-w-0 max-w-[45%] shrink items-center gap-1.5 rounded-full bg-elevated px-2.5 py-1 text-xs text-text-secondary transition-colors hover:text-text-primary md:hidden"
        >
          <WindowIcon size={13} className="shrink-0" />
          <span className="truncate">{snippetTitle}</span>
        </button>
      ) : null}
      <button
        type="button"
        onClick={onOpenSettings}
        aria-label="Settings"
        className="flex h-8 w-8 items-center justify-center rounded-lg text-text-secondary hover:bg-divider/40 hover:text-text-primary"
      >
        <SettingsIcon />
      </button>
    </header>
  );
}
